/** Die Oberfläche im Spiel: Kopfleiste, Schubladen, Verbandskarte, Befehlsleiste, Anzeigen, Tastenkarte. */
import type { hud as en } from '../en/hud';
import type { Translated } from '../types';

export const hud: Translated<typeof en> = {
  // ---- Kopfleiste
  'top.generalStaff': 'Generalstab · {state}',
  'top.inField': 'Im Feld',
  'top.capitulated': 'Kapituliert',
  'top.night': 'Nacht: Sicht −1, Marsch langsamer, Angriffe schwächer',
  'top.daylight': 'Tageslicht',
  'top.wx.over': '{season} · über {region}: {word}, {temp}',
  'top.wx.mapCentre': 'der Kartenmitte',
  'top.wx.ground': 'Boden: {ground}',
  'top.wx.met': 'Wetterdienst (W)',
  'top.hostClock': 'Die Uhr führt der Gastgeber',
  'top.resume': 'Weiter (Leertaste)',
  'top.pause': 'Anhalten (Leertaste)',
  'top.speed': 'Tempo {label} ({v})',
  'top.industry': 'Industrie',
  'top.industry.hint': 'Industrie je Stunde aus versorgten Städten',
  'top.manpower': 'Ersatz',
  'top.manpower.hint': 'Ersatzreserve (tausend Mann)',
  'top.research': 'Forschung',
  'top.research.hint': 'Forschungspunkte je Stunde',
  'top.perHour': '/Std.',
  'top.thousands': ' Tsd.',
  'top.vp.row': '{name}: {vp} SP ({pct})',
  'top.vp.capitulated': ' — kapituliert',
  'top.vp.goal': '{pct} aller {sum} SP 24 Stunden lang halten, um zu siegen.',
  'top.vp.victoryIn': 'Sieg in {h} Std.',
  'top.vp.enemyVictoryIn': 'Feindsieg in {h} Std.',
  'top.vp.label': 'Siegpunkte',
  'top.vp.team': ' · Bündnis {vp}',
  'top.vp.need': ' / {sum} · nötig {pct}',
  'top.menu': 'Stabsmenü (Esc)',
  'hud.paused': 'ANGEHALTEN',

  // ---- Leiste links
  'dock.aria': 'Stabsabteilungen',
  'dock.production': 'Rüst.',
  'dock.research': 'Techn.',
  'dock.intel': 'Feind',
  'dock.weather': 'Wetter',
  'dock.production.title': 'Rüstung',
  'dock.research.title': 'Forschung',
  'dock.intel.title': 'Feindlage',
  'dock.weather.title': 'Wetterdienst',

  // ---- Rüstung
  'prod.title': 'Rüstung',
  'prod.sub': '{rate} Ind./Std. · {men} Tsd. Mann',
  'prod.book': 'Auftragsbuch',
  'prod.lines': '{a}/{b} Linien',
  'prod.idle': 'Die Werke stehen still. Neue Verbände unten in Auftrag geben.',
  'prod.queued': 'wartet',
  'prod.at': 'in {city}',
  'prod.cancel': 'Stornieren (verbrauchte Industrie ist verloren)',
  'prod.deployAt': 'Aufstellen in',
  'prod.city': '{name} · {kind}',
  'prod.capitalCity': '{name} (Hauptstadt) · {kind}',
  'prod.noCity': 'Keine versorgte Stadt — neue Verbände können nicht ausgeliefert werden.',
  'prod.new': 'Neuer Verband',
  'prod.requires': 'Erfordert {tech}',
  'prod.cost': '{type}: {ind} Industrie, {men} Tsd. Mann',
  'prod.men': '{men} Tsd.',

  // ---- Forschung
  'res.title': 'Forschung',
  'res.sub': '{rate} Pkt./Std. · {n}/{total} eingeführt',
  'res.none': 'Kein Forschungsauftrag',
  'res.chooseBelow': 'unten wählen',
  'res.eta': '≈ {eta}',
  'res.requires': 'Erfordert {list}',
  'res.needs': 'Braucht {list}',
  'res.adopted': 'Eingeführt',
  'res.inDevelopment': 'In Entwicklung',

  // ---- Feindlage
  'intel.title': 'Feindlage',
  'intel.sub': { one: 'Bisher {n} Befehl mitgelesen', other: 'Bisher {n} Befehle mitgelesen' },
  'intel.observed': 'Beobachtet',
  'intel.ghosts': 'Altmeldungen',
  'intel.decoys': 'Attrappen',
  'intel.intercepts': 'Abgefangene Feindbefehle',
  'intel.noIntercepts': 'Kein Feindverkehr mitgelesen. Kuriere abfangen oder Entzifferung erforschen.',
  'intel.showPlan': 'Den Plan auf der Karte zeigen',
  'intel.courier': 'KURIER GEFANGEN',
  'intel.radio': 'FUNKSPRUCH ENTZIFFERT',
  'intel.age': 'vor {age} · verfällt in {left}',
  'intel.posture': 'Haltung → {posture}',
  'intel.order': '{order} → {place}',
  'intel.known': 'Bekannte Feindverbände',
  'intel.noContacts': 'Keine Feindverbände in der Kartei.',

  // ---- Wetterdienst
  'met.title': 'Wetterdienst',
  'met.sub': 'Wetterbericht {clock} · T{day}',
  'met.now': 'auf Meereshöhe · nächste 24 Std. {min} … {max} · {wind}',
  'met.chip.title': '{label}: Anteil des Landes jetzt',
  'met.rain': 'Regen',
  'met.mud': 'Schlamm',
  'met.fog': 'Nebel',
  'met.snow': 'Schnee',
  'met.frozen': 'Gefroren',
  'met.outlook': 'Aussichten',
  'met.lead': '+{h} Std.',
  'met.legendNote': 'dunkler = wahrscheinlicher · {clock}',
  'met.clickRegion': 'Eine Region anklicken, um dorthin zu blicken',
  'met.hover': '{region}: {word} {chance} %',
  'met.cell': '{word} · {chance} %',
  'met.regions': 'Regionen',
  'met.fogCol': 'Nebel',
  'met.fogCol.hint': 'Aussicht auf Nebel im Morgengrauen',
  'met.front': 'An unserer Front',
  'met.latest': 'Neues vom Wetterdienst',
  'met.noWarnings': 'Keine Warnungen für unsere Front.',

  // ---- Feldanzeige & Werkzeuge
  'tool.move': 'MARSCH',
  'tool.attack': 'ANGRIFF',
  'tool.retreat': 'RÜCKZUG',
  'tool.move.hint': 'Das Zielfeld anklicken',
  'tool.attack.hint': 'Das anzugreifende Feld anklicken',
  'tool.retreat.hint': 'Anklicken, wohin ausgewichen wird',
  'tool.formations': { one: '{n} Verband', other: '{n} Verbände' },
  'tool.withHHour': ' · mit X-Zeit',
  'tool.keep': 'Werkzeug behalten',
  'tool.cancel': 'abbrechen',
  'hex.vp': '{vp} SP',
  'hex.lastKnown': ' (zuletzt bekannt)',
  'hex.supply': 'Versorgung {pct}',
  'hex.divisions': { one: '{n} Division', other: '{n} Divisionen' },
  'hex.own': '{n} eigene',
  'hex.enemy': '{n} feindliche',
  'hex.unobserved': 'nicht beobachtet',
  'minimap.title': 'Lagekarte',

  // ---- Verbandskarte
  'sel.adoptPosture.aggressive': 'Übergang zu offensiver Haltung',
  'sel.adoptPosture.balanced': 'Übergang zu ausgewogener Haltung',
  'sel.adoptPosture.defensive': 'Übergang zu defensiver Haltung',
  'sel.orderTo': '{order} → {place}',
  'sel.hhourIn': '{hhour} · in {h} Std.',
  'sel.courier.motorcycle': 'Kradmelder unterwegs · ~{eta} Std. · vor {age} abgefahren',
  'sel.courier.horse': 'Meldereiter unterwegs · ~{eta} Std. · vor {age} abgeritten',
  'sel.byWire': 'Per {channel} · trifft in ~{h} Std. ein',
  'sel.issued': 'per {channel} · erteilt {stamp}',
  'sel.encircled': 'Eingekesselt',
  'sel.noSupply': 'Ohne Versorgung',
  'sel.noSupplyFor': 'Ohne Versorgung {h} Std.',
  'sel.inBattle': 'Im Gefecht',
  'sel.dummy': 'Attrappe · gibt sich als {type} aus',
  'sel.strength': 'Stärke',
  'sel.org': 'Zusammenhalt',
  'sel.supply': 'Versorgung',
  'sel.entrenched': 'Eingegraben',
  'sel.experience': 'Erfahrung',
  'sel.currentOrder': 'Laufender Befehl',
  'sel.noOrders': 'Kein Befehl — handelt nach Haltung ({posture}).',
  'sel.awaiting': 'Wartet auf X-Zeit',
  'sel.inTransit': 'Befehle unterwegs',
  'sel.position': 'Standort: {place}.',
  'sel.daysInField': { one: 'Seit {n} Tag im Feld.', other: 'Seit {n} Tagen im Feld.' },
  'sel.miniTitle': '{name} — Klick wählt nur diesen; Umschalt-Klick entfernt ihn',
  'sel.hold.title': 'Halten und eingraben — geht sofort hinaus (H)',
  'sel.tool.title': '{label}: dann das Zielfeld anklicken ({key})',
  'sel.posture': 'Haltung',
  'sel.channel': 'Übermittlung',
  'sel.channel.auto': 'Auto',
  'sel.channel.auto.hint': 'Fernsprecher im Befehlsbereich, sonst Kurier (oder Funk, wenn gewünscht)',
  'sel.channel.courier': 'Kurier',
  'sel.channel.courier.hint': 'Meldereiter auf den Straßen — kann abgefangen werden',
  'sel.channel.radio': 'Funk',
  'sel.channel.radio.hint': 'Schnell, doch feindliche Entzifferer können mitlesen',
  'sel.channel.radio.locked': 'Erfordert Feldfunk',
  'sel.hhour': 'X-Zeit',
  'sel.hhour.now': 'Sofort',
  'sel.hhour.now.hint': 'Bei Eingang ausführen',
  // The H-hour control is narrow: the SI hour ("+6 h") as on German staff time tables.
  'sel.hhour.plus': '+{h} h',
  'sel.hhour.dawn': '0500',
  'sel.hhour.dawn.hint': 'Nächstes Morgengrauen, 05.00 Uhr',
  'sel.hhour.custom.hint': 'Tag und Stunde wählen',
  'sel.hhour.day': 'Tag',
  'sel.hhour.at': 'um',
  'sel.hhour.dayAria': 'Tag der X-Zeit',
  'sel.hhour.hourAria': 'X-Zeit',
  'sel.hhour.night': ' · nachts',
  'sel.operation': 'Unternehmen',
  'sel.operation.placeholder': 'Ohne Namen',
  'sel.operation.aria': 'Deckname des Unternehmens',
  'sel.operation.suggest': 'Einen Decknamen vorschlagen',
  'sel.level.1': 'Kontakt',
  'sel.level.2': 'Erkannt',
  'sel.level.3': 'Aufgeklärt',
  'sel.lastSeen': 'Zuletzt gesehen',
  'sel.observedNow': 'Jetzt unter Beobachtung',
  'sel.seenAgo': 'vor {age} ({stamp})',
  'sel.contactPosition': 'Standort',
  'sel.believed': ' (vermutet)',
  'sel.type': 'Art',
  'sel.dummyFormation': 'Scheinverband',
  'sel.unknown': 'Unbekannt',
  'sel.heading': 'Richtung',
  'sel.headingVal': 'Zuletzt nach {dir} in Bewegung',
  'sel.estimate': '≈{pct}',
  'sel.decoyHint': 'Zeltbahn und Sperrholz. Unsere Spähtrupps kamen nah genug heran, um es zu durchschauen.',
  'sel.unknownStrength':
    'Stärke unbekannt. Einen Verband heranführen — oder Aufklärung —, um den Feind aufzuklären.',
  'sel.oldReport': 'Diese Meldung ist {age} alt. Der Verband kann weitergezogen sein.',
  'sel.formations': { one: '{n} Verband', other: '{n} Verbände' },
  'sel.typeCount': '{n} × {type}',
  'sel.allegiance': '{adj} · {name}',
  'sel.unknownAllegiance': 'Zugehörigkeit unbekannt',
  'sel.observed': 'Beobachtet',
  'sel.ghost': 'Altmeldung · {age}',
  'sel.decoyStamp': 'Attrappe',
  'sel.attackHint': 'Eigene Verbände wählen und diesen Kontakt mit Rechtsklick angreifen.',

  // ---- Fernschreiber
  'tty.aria': 'Meldungen',
  'tty.title': 'Fernschreiber',
  'tty.filter.all': 'Alle',
  'tty.filter.front': 'Front',
  'tty.filter.orders': 'Befehle',
  'tty.filter.intel': 'Feind',
  'tty.filter.home': 'Heimat',
  'tty.collapse': 'Einklappen',
  'tty.expand': 'Verlauf ausklappen',
  'tty.dayRule': 'TAG {n}',
  'tty.empty': '— LEITUNG FREI · ERWARTE VERKEHR —',

  // ---- Telegramm & Zeitung
  'tg.head': 'TELEGRAMM · GENERALSTAB',
  'paper.aria': '{masthead}, Tag {n}',
  'paper.putAway': 'Klicken, um die Zeitung wegzulegen',
  'paper.edition': 'Morgenausgabe',
  'paper.no': 'Nr. {n}',
  'paper.dateline': 'Tag {n} · 6 Uhr früh',
  'paper.price': 'Preis 5',

  // ---- Überlagerungen
  'staff.kicker': 'STABSBESPRECHUNG',
  'staff.title': 'Das Lagezimmer',
  'staff.clockStopped': ' · Uhr angehalten',
  'staff.return': 'Zurück zur Karte',
  'staff.manual': 'Felddienstordnung',
  'staff.hotkeys': 'Tastenbelegung',
  'staff.settings': 'Einstellungen',
  'staff.capitulate': 'Kapitulation anbieten',
  'staff.abandon': 'Feldzug aufgeben',
  'staff.leave': 'Den Krieg verlassen',
  'sheet.manual': 'Felddienstordnung',
  'sheet.settings': 'Einstellungen',
  'keys.title': 'STEHENDE BEFEHLE · TASTATUR',
  'keys.foot': 'Auf der Karte ziehen, um mehrere zu wählen · Esc schließt diese Karte',
  'surrender.title': 'KAPITULATION ANBIETEN?',
  'surrender.text':
    'Unsere Truppen werden die Waffen niederlegen, unser Gebiet fällt an die Besatzer. Das lässt sich nicht rückgängig machen.',
  'surrender.fight': 'Weiterkämpfen',
  'surrender.capitulate': 'Kapitulieren',

  // ---- Tastenbelegung
  'hk.pause': 'Anhalten / weiter',
  'hk.speed': 'Spieltempo',
  'hk.move': 'Marschwerkzeug',
  'hk.attack': 'Angriffswerkzeug',
  'hk.retreat': 'Rückzugswerkzeug',
  'hk.hold': 'Halten & eingraben (geht sofort hinaus)',
  'hk.focus': 'Kamera auf die Auswahl',
  'hk.esc': 'Werkzeug abbrechen · Auswahl aufheben · Menü',
  'hk.production': 'Rüstung',
  'hk.research': 'Forschung',
  'hk.intel': 'Feindlage',
  'hk.met': 'Wetterdienst (Wetter & Aussichten)',
  'hk.card': 'Diese Karte',
  'hk.rightClick': 'Marsch / Angriff auf das angeklickte Feld',
  'hk.shiftClick': 'Zur Auswahl hinzufügen · Werkzeug behalten',
  'hk.pan': 'Karte verschieben (Umschalt = schneller)',
  'hk.zoom': 'Zum Mauszeiger zoomen',
  'key.space': 'Leertaste',
  'key.shift': 'Umschalt',
  'key.click': 'Klick',
  'key.rightClick': 'Rechtsklick',
  'key.arrows': 'Pfeiltasten',
  'key.rightDrag': 'Rechts ziehen',
  'key.wheel': 'Mausrad',
};
